import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, ArrowRight, ArrowUpRight, Users } from 'lucide-react'
import { FaWhatsapp } from 'react-icons/fa'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import SectionHeading from '@/components/SectionHeading'
import WeatherWidget from '@/components/WeatherWidget'
import { hotel, whatsappLink } from '@/data/hotel'
import { rooms } from '@/data/rooms'
import { gsap, SplitText, prefersReducedMotion, transition, useGSAP } from '@/lib/gsap'
import { setupReveals } from '@/lib/reveal'

const stats = [
  { value: new Date().getFullYear() - hotel.founded, label: 'anos recebendo hóspedes' },
  { value: rooms.length, label: 'tipos de acomodação' },
  { value: 24, suffix: 'h', label: 'de recepção, todos os dias' },
  { value: 220, prefix: 'R$ ', label: 'diária a partir de' },
]

function RoomCard({ room, index }) {
  return (
    <article
      data-reveal
      className="group flex shrink-0 flex-col overflow-hidden rounded-3xl bg-card shadow-xl ring-1 shadow-ink/5 ring-foreground/5 w-[82%] max-w-[360px] sm:w-[46%] lg:w-[min(31%,380px)]"
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img
          src={room.images[0]}
          alt={room.title}
          draggable={false}
          className="size-full object-cover transition-[scale] duration-1000 ease-out group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
        <div className="absolute inset-x-5 top-5 flex items-start justify-between">
          {room.capacity ? (
            <Badge variant="glass" className="h-7 bg-ink/40 px-3">
              <Users data-icon="inline-start" />
              {room.capacity}
            </Badge>
          ) : (
            <span />
          )}
          <span className="font-heading text-lg text-ink-foreground/80">
            {String(index + 1).padStart(2, '0')}
          </span>
        </div>
        <div className="absolute inset-x-6 bottom-6 flex flex-col gap-1.5 text-ink-foreground">
          <span className="text-xs font-bold tracking-[0.2em] text-highlight uppercase">
            {room.tagline}
          </span>
          <h3 className="text-3xl font-bold">{room.title}</h3>
        </div>
      </div>
      <div className="flex items-center justify-between gap-4 p-6">
        <div>
          <p className="font-heading text-2xl font-bold text-primary">{room.price}</p>
          <p className="text-xs text-muted-foreground">{room.priceNote}</p>
        </div>
        <Button asChild size="icon-lg" variant="outline" className="size-11 rounded-full">
          <Link to={`/galeria#${room.id}`} aria-label={`Ver detalhes do ${room.title}`}>
            <ArrowUpRight className="transition-transform duration-300 group-hover:rotate-45" />
          </Link>
        </Button>
      </div>
    </article>
  )
}

function RoomsIntro() {
  return (
    <div className="flex flex-col items-start gap-6">
      <SectionHeading
        align="start"
        eyebrow="Acomodações"
        title="Nossos quartos"
        description="Opções para quem viaja sozinho, casais, famílias e estadias mais longas, todas com café da manhã e Wi-Fi."
      />
      <Button data-reveal asChild variant="outline" size="xl">
        <Link to="/galeria">
          Ver todos os detalhes
          <ArrowRight data-icon="inline-end" />
        </Link>
      </Button>
    </div>
  )
}

function HomePage() {
  const root = useRef(null)
  const roomsTrack = useRef(null)

  // Setas do carrossel de quartos: avança/volta um card
  const scrollRooms = (direction) => {
    const track = roomsTrack.current
    const card = track?.querySelector('article')
    if (!card) return
    const gap = parseFloat(getComputedStyle(track).columnGap || 0)
    track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: 'smooth' })
  }

  // Arrastar o carrossel com o mouse (no toque e no touchpad o navegador já faz isso sozinho)
  const drag = useRef(null)
  const dragHandlers = {
    onPointerDown: (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return
      const track = roomsTrack.current
      drag.current = { x: e.clientX, left: track.scrollLeft, moved: false }
    },
    onPointerMove: (e) => {
      if (!drag.current) return
      const track = roomsTrack.current
      const dx = e.clientX - drag.current.x
      if (!drag.current.moved && Math.abs(dx) > 5) {
        drag.current.moved = true
        track.style.cursor = 'grabbing'
      }
      if (drag.current.moved) track.scrollLeft = drag.current.left - dx
    },
    onPointerUp: () => endDrag(),
    onPointerLeave: () => endDrag(),
    // Não abre o link do card se o usuário estava arrastando
    onClickCapture: (e) => {
      if (drag.current?.moved || roomsTrack.current.dataset.dragged) {
        e.preventDefault()
        e.stopPropagation()
      }
      delete roomsTrack.current.dataset.dragged
    },
  }
  const endDrag = () => {
    if (!drag.current) return
    if (drag.current.moved) {
      roomsTrack.current.dataset.dragged = 'true'
      roomsTrack.current.style.cursor = ''
    }
    drag.current = null
  }

  useGSAP(
    () => {
      if (!prefersReducedMotion()) {
        // Hero: zoom de entrada na foto e título subindo palavra por palavra
        const title = SplitText.create('.hero-title', { type: 'words', mask: 'words' })
        gsap
          .timeline({ delay: transition.delay })
          .from('.hero-media', { scale: 1.3, duration: 2.4, ease: 'expo.out' }, 0)
          .from('.hero-eyebrow', { autoAlpha: 0, y: 20 }, 0.2)
          .from(
            title.words,
            {
              yPercent: 110,
              duration: 1.2,
              ease: 'expo.out',
              stagger: 0.07,
              onComplete: () => title.revert(),
            },
            0.3
          )
          .from('.hero-fade', { autoAlpha: 0, y: 24, stagger: 0.12 }, 0.85)
          .from('.hero-scroll', { autoAlpha: 0, duration: 1 }, 1.4)

        gsap
          .timeline({ repeat: -1 })
          .fromTo(
            '.hero-scroll-line',
            { scaleY: 0, transformOrigin: 'top' },
            { scaleY: 1, duration: 0.9, ease: 'power2.inOut' }
          )
          .to('.hero-scroll-line', { scaleY: 0, transformOrigin: 'bottom', duration: 0.9, ease: 'power2.inOut' })

        // Hero: parallax ao rolar
        const heroScroll = { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true }
        gsap.to('.hero-media', { yPercent: 18, ease: 'none', scrollTrigger: heroScroll })
        gsap.to('.hero-content', {
          yPercent: -30,
          opacity: 0,
          ease: 'none',
          scrollTrigger: { ...heroScroll, end: '75% top' },
        })

        // Chamada final: texto gigante deslizando ao fundo
        gsap.fromTo(
          '.cta-giant',
          { xPercent: 5 },
          {
            xPercent: -40,
            ease: 'none',
            scrollTrigger: { trigger: '.cta', start: 'top bottom', end: 'bottom top', scrub: true },
          }
        )
      }

      setupReveals(root.current)
    },
    { scope: root }
  )

  return (
    <div ref={root}>
      {/* Hero */}
      <section className="hero relative isolate flex h-svh min-h-[640px] items-end overflow-hidden pb-28 md:items-center md:pb-0">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <img
            src="/images/hotel-banner.jpg"
            alt="Fachada do Star Hotel"
            className="hero-media size-full object-cover"
          />
        </div>
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-ink via-ink/70 to-ink/30 md:bg-gradient-to-r md:from-ink/95 md:via-ink/60 md:to-ink/20" />

        <div className="hero-content container-page">
          <div className="flex max-w-3xl flex-col items-start gap-7 text-ink-foreground">
            <span className="hero-eyebrow flex items-center gap-3 text-xs font-bold tracking-[0.3em] text-highlight uppercase">
              <span className="h-px w-10 bg-highlight" />
              Star Hotel · Limoeiro, PE
            </span>
            <h1 className="hero-title text-5xl leading-[1.02] font-bold sm:text-6xl md:text-8xl">
              Sua estadia perfeita em <em className="text-highlight">Limoeiro</em>
            </h1>
            <p className="hero-fade max-w-xl text-lg text-ink-foreground/80 sm:text-xl">
              Conforto, elegância e um atendimento que faz você se sentir em casa.
            </p>
            <div className="hero-fade flex flex-wrap gap-3 pt-2">
              <Button asChild variant="highlight" size="xl">
                <Link to="/galeria">
                  Conheça nossos quartos
                  <ArrowRight data-icon="inline-end" />
                </Link>
              </Button>
              <Button asChild variant="glass" size="xl">
                <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
                  <FaWhatsapp data-icon="inline-start" />
                  Reservar agora
                </a>
              </Button>
            </div>
          </div>
        </div>

        <div className="hero-scroll absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 text-ink-foreground/70 [@media(min-height:820px)]:md:flex">
          <span className="text-[0.65rem] font-bold tracking-[0.35em] uppercase">Role</span>
          <span className="relative h-14 w-px overflow-hidden bg-ink-foreground/20">
            <span className="hero-scroll-line absolute inset-0 bg-highlight" />
          </span>
        </div>
      </section>

      {/* Números */}
      <section className="py-20 md:py-28">
        <div className="container-page">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-12 lg:grid-cols-4">
            {stats.map(({ value, prefix, suffix, label }) => (
              <div key={label} data-reveal className="flex flex-col gap-2">
                <dt className="sr-only">{label}</dt>
                <dd className="font-heading text-5xl font-bold text-primary md:text-6xl">
                  {prefix}
                  <span data-counter={value}>{value}</span>
                  {suffix}
                </dd>
                <dd className="text-sm text-muted-foreground">{label}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Quartos: carrossel que se arrasta para o lado (dedo, touchpad, mouse ou setas) */}
      <section className="overflow-hidden bg-secondary/60 py-20 md:py-28">
        <div className="container-page mb-10 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <RoomsIntro />
          <div className="hidden gap-2 md:flex">
            <Button variant="outline" size="icon-lg" className="size-11 rounded-full" onClick={() => scrollRooms(-1)} aria-label="Quarto anterior">
              <ArrowLeft />
            </Button>
            <Button variant="outline" size="icon-lg" className="size-11 rounded-full" onClick={() => scrollRooms(1)} aria-label="Próximo quarto">
              <ArrowRight />
            </Button>
          </div>
        </div>

        <div
          ref={roomsTrack}
          {...dragHandlers}
          className="flex gap-4 overflow-x-auto overscroll-x-contain px-4 pb-6 select-none [scrollbar-width:none] sm:px-6 md:cursor-grab md:gap-6 lg:px-[max(2rem,calc((100%-80rem)/2+2rem))] [&::-webkit-scrollbar]:hidden"
        >
          {rooms.map((room, index) => (
            <RoomCard key={room.id} room={room} index={index} />
          ))}
        </div>

        <p className="container-page flex items-center gap-2 text-sm text-muted-foreground md:hidden">
          Arraste para o lado para ver todos
          <ArrowRight className="size-4" />
        </p>
      </section>

      {/* Sobre em destaque */}
      <section className="py-24 md:py-36">
        <div className="container-page grid grid-cols-1 items-center gap-16 md:grid-cols-2 md:gap-20">
          <div className="relative">
            <div data-clip className="relative h-[460px] overflow-hidden rounded-3xl shadow-2xl shadow-ink/15">
              <img
                data-parallax="8"
                src="/images/hotel-banner.jpg"
                alt="Fachada do Star Hotel"
                className="absolute inset-0 size-full object-cover"
              />
            </div>
            <div
              data-reveal
              className="absolute -top-6 -right-4 flex flex-col items-center gap-1 rounded-2xl bg-highlight px-7 py-5 text-highlight-foreground shadow-xl"
            >
              <span className="text-xs font-bold tracking-wide uppercase">Desde</span>
              <span className="font-heading text-4xl leading-none font-bold">{hotel.founded}</span>
            </div>
          </div>
          <div className="flex flex-col items-start gap-6">
            <SectionHeading
              align="start"
              eyebrow="Nossa história"
              title="Tradição e acolhimento em Limoeiro"
              description={`Fundado em ${hotel.founded}, o Star Hotel nasceu do desejo de proporcionar uma experiência única e acolhedora aos nossos visitantes, unindo conforto, bem-estar e a hospitalidade típica de Limoeiro-PE.`}
            />
            <Button data-reveal asChild variant="outline" size="xl">
              <Link to="/sobre">
                Conheça nossa história
                <ArrowRight data-icon="inline-end" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Clima */}
      <section className="bg-secondary/60 py-24 md:py-28">
        <div className="container-page flex flex-col gap-12">
          <SectionHeading eyebrow="Planeje sua viagem" title="Clima em Limoeiro" />
          <div data-reveal className="mx-auto w-full max-w-md">
            <WeatherWidget />
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="cta relative isolate overflow-hidden bg-ink-texture py-28 text-ink-foreground md:py-40">
        <div
          aria-hidden="true"
          className="cta-giant text-outline pointer-events-none absolute top-1/2 left-0 -z-10 -translate-y-1/2 font-heading text-[22vw] leading-none font-bold whitespace-nowrap select-none"
        >
          Star Hotel · Limoeiro · Star Hotel
        </div>
        <div className="container-page flex max-w-3xl flex-col items-center gap-6 text-center">
          <span data-reveal className="eyebrow text-highlight">
            Reservas
          </span>
          <h2 data-split className="text-4xl font-bold md:text-6xl">
            Pronto para viver essa experiência?
          </h2>
          <p data-reveal className="text-lg text-ink-foreground/75">
            Fale com a gente e garanta sua reserva no Star Hotel.
          </p>
          <Button data-reveal asChild variant="highlight" size="xl" className="mt-2">
            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
              <FaWhatsapp data-icon="inline-start" />
              Reservar pelo WhatsApp
            </a>
          </Button>
        </div>
      </section>
    </div>
  )
}

export default HomePage
